import React, { useState, useEffect } from 'react';
import { dataService } from '../../services/dataService';
import { Question, Standard, CsvImport } from '../../data/types';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../layouts/PageContainer';
import TabContainer from '../layouts/TabContainer';
import ContentContainer from '../layouts/ContentContainer';

const SuperAdminQuestionManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'questions' | 'import'>('questions');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [filterStandard, setFilterStandard] = useState<string>('all');
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterDifficulty, setFilterDifficulty] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recentImport, setRecentImport] = useState<CsvImport | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [questionsData, standardsData, importsData] = await Promise.all([
          dataService.getQuestions(),
          dataService.getStandards(),
          dataService.getCsvImports()
        ]);
        
        setQuestions(questionsData);
        setStandards(standardsData);
        
        // Get the most recent question import
        const questionImports = importsData.filter(imp => imp.type === 'Questions');
        if (questionImports.length > 0) {
          const sortedImports = questionImports.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          setRecentImport(sortedImports[0]);
        }
      } catch (error) {
        console.error('Error fetching question management data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle file upload for questions
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadStatus('uploading');
      const importResult = await dataService.uploadQuestions(file);
      setRecentImport(importResult);
      setUploadStatus('success');
      
      // Refresh questions list after a short delay to simulate processing
      setTimeout(async () => {
        const questionsData = await dataService.getQuestions();
        setQuestions(questionsData);
      }, 2000);
      
      // Clear status after 3 seconds
      setTimeout(() => setUploadStatus('idle'), 3000);
    } catch (error) {
      console.error('Error uploading questions:', error);
      setUploadStatus('error');
      
      // Clear status after 3 seconds
      setTimeout(() => setUploadStatus('idle'), 3000);
    }
  };

  // Handle question deletion
  const handleDeleteQuestions = async () => {
    if (selectedQuestions.length === 0) return;
    
    try {
      // Delete each selected question
      for (const questionId of selectedQuestions) {
        await dataService.removeQuestion(questionId);
      }
      
      // Refresh questions list
      const questionsData = await dataService.getQuestions();
      setQuestions(questionsData);
      
      // Reset selection and confirmation
      setSelectedQuestions([]);
      setConfirmDelete(false);
    } catch (error) {
      console.error('Error deleting questions:', error);
    }
  };

  // Toggle question selection
  const toggleQuestionSelection = (questionId: string) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };

  // Select/deselect all visible questions
  const toggleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) {
      setSelectedQuestions([]);
    } else {
      setSelectedQuestions(filteredQuestions.map(q => q.id));
    }
  };

  // Format timestamp to readable date/time
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Filter questions based on criteria
  const filteredQuestions = questions.filter(question => {
    // Filter by standard
    if (filterStandard !== 'all' && question.standardId !== filterStandard) {
      return false;
    }
    
    // Filter by grade (via standard)
    if (filterGrade !== 'all') {
      const standard = standards.find(s => s.id === question.standardId);
      if (!standard || standard.grade !== filterGrade) {
        return false;
      }
    }
    
    // Filter by difficulty
    if (filterDifficulty !== 'all' && question.difficulty !== filterDifficulty) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery && !question.text.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Reset filters to default values
  const resetFilters = () => {
    setFilterStandard('all');
    setFilterGrade('all');
    setFilterDifficulty('all');
    setSearchQuery('');
  };

  // Get all unique grades from standards
  const uniqueGrades = Array.from(new Set(standards.map(s => s.grade)))
    .sort((a, b) => {
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });

  // Get all unique difficulties from questions
  const uniqueDifficulties = Array.from(new Set(questions.map(q => q.difficulty)));

  // Get standard name by ID
  const getStandardName = (standardId: string) => {
    const standard = standards.find(s => s.id === standardId);
    return standard ? standard.code : 'Unknown';
  };

  // Get standard grade by ID
  const getStandardGrade = (standardId: string) => {
    const standard = standards.find(s => s.id === standardId);
    return standard ? standard.grade : 'Unknown';
  };

  // Get difficulty badge with appropriate color
  const getDifficultyBadge = (difficulty: string) => {
    let bgColor = '';
    let textColor = '';
    
    switch(difficulty) {
      case 'Easy':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Medium':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Hard':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
    }
    
    return (
      <span className={`${bgColor} ${textColor} px-2 py-1 rounded-full text-xs font-medium`}>
        {difficulty}
      </span>
    );
  };

  // CSV Import Tab Content
  const renderImportTab = () => {
    return (
      <ContentContainer>
        <div className="space-y-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Import Questions from CSV</h2>
          
          {uploadStatus === 'uploading' && (
            <div className="mb-4 p-3 rounded bg-blue-50 text-blue-700">
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Uploading and processing your questions...</span>
              </div>
            </div>
          )}
          
          {uploadStatus === 'success' && (
            <div className="mb-4 p-3 rounded bg-green-50 text-green-700">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-3 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>Questions uploaded successfully!</span>
              </div>
            </div>
          )}
          
          {uploadStatus === 'error' && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-700">
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-3 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                <span>Error uploading questions. Please try again.</span>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-center border-2 border-gray-300 border-dashed rounded-md px-6 pt-5 pb-6">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none"
                >
                  <span>Upload a CSV file</span>
                  <input 
                    id="file-upload" 
                    name="file-upload" 
                    type="file" 
                    className="sr-only" 
                    accept=".csv"
                    onChange={handleFileUpload}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">CSV up to 10MB</p>
            </div>
          </div>
          
          <div className="mt-4">
            <button
              className="text-sm text-primary hover:text-primary-dark flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Download CSV Template
            </button>
          </div>
          
          {recentImport && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Import</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">File:</span>
                  <span className="text-sm text-gray-600">{recentImport.filename}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Date:</span>
                  <span className="text-sm text-gray-600">{formatTimestamp(recentImport.timestamp)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span className={`text-sm ${
                    recentImport.status === 'success' ? 'text-green-600' : 
                    recentImport.status === 'failed' ? 'text-red-600' : 
                    recentImport.status === 'partial' ? 'text-yellow-600' : 'text-blue-600'
                  }`}>
                    {recentImport.status.charAt(0).toUpperCase() + recentImport.status.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Records:</span>
                  <span className="text-sm text-gray-600">
                    {recentImport.recordsSucceeded} of {recentImport.recordsProcessed} successful
                  </span>
                </div>
                
                {recentImport.errors.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-gray-700">Errors:</span>
                    <div className="mt-1 max-h-40 overflow-y-auto">
                      {recentImport.errors.map((error, index) => (
                        <div key={index} className="text-xs text-red-600 mb-1">
                          Row {error.row}: {error.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ContentContainer>
    );
  };

  // Questions Tab Content
  const renderQuestionsTab = () => {
    return (
      <ContentContainer>
        <div className="flex flex-col sm:flex-row justify-between mb-6 space-y-4 sm:space-y-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search questions..."
                className="pl-10 pr-4 py-1.5 border border-gray-300 rounded-md w-full sm:w-64 text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            
            {/* Standard Filter */}
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 w-full sm:w-auto text-sm"
              value={filterStandard}
              onChange={(e) => setFilterStandard(e.target.value)}
            >
              <option value="all">All Standards</option>
              {standards.map(standard => (
                <option key={standard.id} value={standard.id}>
                  {standard.code}
                </option>
              ))}
            </select>
            
            {/* Grade Filter */}
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 w-full sm:w-auto text-sm"
              value={filterGrade}
              onChange={(e) => setFilterGrade(e.target.value)}
            >
              <option value="all">All Grades</option>
              {uniqueGrades.map(grade => (
                <option key={grade} value={grade}>
                  Grade {grade}
                </option>
              ))}
            </select>
            
            {/* Difficulty Filter */}
            <select
              className="border border-gray-300 rounded-md px-3 py-1.5 w-full sm:w-auto text-sm"
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
            >
              <option value="all">All Difficulties</option>
              {uniqueDifficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>
                  {difficulty}
                </option>
              ))}
            </select>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 flex items-center"
              onClick={() => navigate('/questions/create')}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create Question
            </button>
            
            <button
              className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium bg-primary text-white hover:bg-primary-dark"
              onClick={() => setActiveTab('import')}
            >
              Import Questions
            </button>
            
            <button
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
              onClick={resetFilters}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
          </div>
        </div>
        
        {selectedQuestions.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
            <span className="text-sm text-gray-700">
              {selectedQuestions.length} question{selectedQuestions.length > 1 ? 's' : ''} selected
            </span>
            <button
              className="px-3 py-1.5 bg-red-50 text-red-700 rounded-md text-sm font-medium hover:bg-red-100"
              onClick={() => setConfirmDelete(true)}
            >
              Delete Selected
            </button>
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No questions found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-10 px-3 py-2 text-center">
                    <div className="flex justify-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        checked={selectedQuestions.length === filteredQuestions.length && filteredQuestions.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </div>
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standard</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuestions.map((question) => (
                  <tr key={question.id} className="hover:bg-gray-50">
                    <td className="w-10 px-3 py-2 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => toggleQuestionSelection(question.id)}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div 
                        className="text-sm text-gray-900 max-w-md truncate cursor-pointer hover:text-primary"
                        onClick={() => navigate(`/questions/${question.id}/edit`)}
                      >
                        {question.text}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {getStandardName(question.standardId)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {getStandardGrade(question.standardId)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {getDifficultyBadge(question.difficulty)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {question.type}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right">
                      <button 
                        onClick={() => navigate(`/questions/${question.id}/edit`)} 
                        className="text-primary hover:text-primary-dark font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {filteredQuestions.length > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-gray-500">
              Showing {filteredQuestions.length} of {questions.length} questions
            </span>
            <div className="flex space-x-2">
              <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Previous
              </button>
              <button className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium bg-primary text-white hover:bg-primary-dark">
                Next
              </button>
            </div>
          </div>
        )}
      </ContentContainer>
    );
  };

  return (
    <PageContainer title="Question Management" userRole="SuperAdmin">
      <TabContainer 
        tabContent={
          <div className="flex border-b border-b-transparent">
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'questions' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('questions')}
            >
              Questions
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm border-b-2 ${activeTab === 'import' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('import')}
            >
              Import Questions
            </button>
          </div>
        }
      >
        {activeTab === 'questions' ? renderQuestionsTab() : renderImportTab()}
      </TabContainer>
      
      {/* Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {selectedQuestions.length} selected question{selectedQuestions.length > 1 ? 's' : ''}? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                onClick={handleDeleteQuestions}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default SuperAdminQuestionManagementScreen;
